import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Element | null>} O'Neill: color-variations markup per product URL. */
  #cachedColorVariations = new Map();
  /** @type {Map<string, string | undefined>} O'Neill: product title per URL (mobile header). */
  #cachedTitle = new Map();
  /** @type {AbortController | null} O'Neill: listeners for the mobile slider dots. */
  #dotsAbortController = null;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#dotsAbortController?.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#cachedColorVariations.clear();
    this.#cachedTitle.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    await this.#renderForUrl(this.productPageUrl);
    this.#openQuickAddModal();
  };

  /**
   * Fetches (or reuses cached) product-grid content for a URL, renders it into the
   * quick-add modal, and inserts the color-variations row below the buy buttons.
   * Called both for the initial open and when a color-variation thumbnail inside
   * the modal is clicked (re-rendering the modal for that colour). O'Neill Canada.
   * @param {string} url - The product page URL to render.
   * @param {string} [title] - Optional product title (used for the mobile header).
   */
  async #renderForUrl(url, title) {
    let productGrid = this.#cachedContent.get(url);
    let colorVariations = this.#cachedColorVariations.get(url);
    let resolvedTitle = title ?? this.#cachedTitle.get(url);

    if (!productGrid) {
      const html = await this.fetchProductPage(url);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(url, productGrid);
        }

        const colorVariationsElement = html.querySelector('.color-variations');
        colorVariations = colorVariationsElement
          ? /** @type {Element} */ (colorVariationsElement.cloneNode(true))
          : null;
        this.#cachedColorVariations.set(url, colorVariations);

        resolvedTitle = resolvedTitle ?? html.querySelector('.view-product-title a')?.textContent?.trim();
        this.#cachedTitle.set(url, resolvedTitle);
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent, { url, title: resolvedTitle });
      this.#updateVariantPicker(productGrid);
      this.#insertColorVariations(colorVariations);
    }
  }

  /**
   * Inserts the color-variations row directly below the buy buttons in the modal,
   * replacing any copy that arrived inline with the product grid (which sits at its
   * PDP position and is otherwise hidden by the quick-add modal styles). Inside the
   * modal the thumbnails re-render the quick-add for the chosen colour instead of
   * navigating to the PDP. O'Neill Canada customization.
   * @param {Element | null | undefined} colorVariations
   */
  #insertColorVariations(colorVariations) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Remove any inline copy that came along with the product grid.
    for (const existing of modalContent.querySelectorAll('.color-variations')) {
      existing.remove();
    }

    if (!colorVariations) return;

    const clone = /** @type {Element} */ (colorVariations.cloneNode(true));
    clone.classList.add('color-variations--quick-add');

    // Each fetched colour's PDP renders its own thumbnail first, which would make the
    // clicked colour jump to slot 1 on every switch. Sort by the stable per-product key
    // so thumbnails keep a fixed position and only the outline marks the current colour.
    const list = clone.querySelector('.color-variations__list');
    if (list) {
      const items = Array.from(list.children).filter((el) => el instanceof HTMLElement);
      items.sort((a, b) =>
        (a.getAttribute('data-color-key') || '').localeCompare(b.getAttribute('data-color-key') || '')
      );
      for (const item of items) list.appendChild(item);
    }

    clone.addEventListener('click', this.#handleColorVariationClick);

    const productForm = modalContent.querySelector('product-form-component');
    const anchor = productForm?.closest('.buy-buttons-block') ?? productForm;

    if (anchor?.parentNode) {
      anchor.insertAdjacentElement('afterend', clone);
    } else {
      modalContent.appendChild(clone);
    }

    // On mobile, collapse the colour row to one line with a "View all" toggle.
    // (Also re-run after the dialog opens -- see #openQuickAddModal -- because a
    // closed <dialog> has no layout to measure.)
    if (isMobileBreakpoint()) {
      requestAnimationFrame(() => this.#setupColorVariationsExpand());
    }
  }

  /**
   * On mobile, keeps the colour-variations row to a single line and, when the
   * thumbnails overflow, adds a "View all colours" button that expands to reveal
   * the rest. Queries the current colour row so it can run both after insert and
   * after the modal opens (idempotent). O'Neill Canada customization.
   */
  #setupColorVariationsExpand() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const container = modalContent?.querySelector('.color-variations--quick-add');
    if (!(container instanceof HTMLElement)) return;

    const list = container.querySelector('.color-variations__list');
    if (!(list instanceof HTMLElement)) return;

    // Reset any previous setup.
    container.querySelector('.color-variations__view-all')?.remove();
    container.classList.remove('is-truncated', 'is-expanded');
    const items = /** @type {HTMLElement[]} */ (Array.from(list.children).filter((el) => el instanceof HTMLElement));
    for (const item of items) item.style.display = '';

    if (list.clientWidth === 0 || items.length <= 1) return;

    // How many WHOLE thumbnails fit on one line.
    const styles = getComputedStyle(list);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const itemWidth = items[0].getBoundingClientRect().width;
    if (!itemWidth) return;
    const fit = Math.max(1, Math.floor((list.clientWidth + gap) / (itemWidth + gap)));

    if (items.length <= fit) return; // everything fits -> no toggle needed

    // Collapsed: show only the whole thumbnails that fit (no partial, no fade).
    items.forEach((item, i) => {
      item.style.display = i < fit ? '' : 'none';
    });
    container.classList.add('is-truncated');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'color-variations__view-all';
    button.textContent = /** @type {HTMLElement} */ (container).dataset.viewAllLabel || 'View all colours';
    button.addEventListener(
      'click',
      () => {
        const media = modalContent?.querySelector('.product-information__media');
        if (media instanceof HTMLElement) {
          // Freeze the image so expanding the colours doesn't shrink it; scroll instead.
          media.style.height = `${media.getBoundingClientRect().height}px`;
          media.style.flex = '0 0 auto';
        }
        modalContent?.classList.add('is-colors-expanded');
        for (const item of items) item.style.display = '';
        container.classList.add('is-expanded');
      },
      { once: true }
    );
    container.appendChild(button);
  }

  /**
   * Re-renders the quick-add modal for a clicked colour-variation thumbnail instead
   * of following its link to the PDP. O'Neill Canada customization.
   * @param {Event} event
   */
  #handleColorVariationClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = /** @type {HTMLAnchorElement | null} */ (target?.closest('a[href]') ?? null);
    if (!link) return;
    event.preventDefault();
    // Re-rendering morphs the modal synchronously (for cached colours), which detaches
    // this click target before the event finishes bubbling. Without stopping propagation
    // the dialog's outside-click handler then sees a detached target and closes the popup.
    event.stopPropagation();
    this.#renderForUrl(link.href);
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener(
      'animationstart',
      () => {
        this.#resetScroll();
        // Now that the dialog is laid out, (re-)evaluate the colour-row overflow.
        if (isMobileBreakpoint()) requestAnimationFrame(() => this.#setupColorVariationsExpand());
      },
      { once: true }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid, { url, title } = {}) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = title || this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = url || this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    // O'Neill: on mobile the media is a swipeable slider -- add position dots.
    if (isMobileBreakpoint()) {
      requestAnimationFrame(() => this.#buildSliderDots());
    }
  }

  /**
   * Builds swipe-indicator dots over the mobile quick-add image slider and keeps
   * the active dot in sync with the slider's scroll position. Rebuilt on each
   * render; does nothing for single-image products. O'Neill Canada.
   */
  #buildSliderDots() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const media = modalContent.querySelector('.product-information__media');
    if (!media) return;

    // Clear any previous dots + their listeners.
    media.querySelector('.qa-dots')?.remove();
    this.#dotsAbortController?.abort();

    const scroller = /** @type {HTMLElement | null} */ (media.querySelector('slideshow-slides'));
    const slides = scroller ? scroller.querySelectorAll('slideshow-slide') : [];
    if (!scroller || slides.length <= 1) return;

    this.#dotsAbortController = new AbortController();
    const { signal } = this.#dotsAbortController;

    const dots = document.createElement('div');
    dots.className = 'qa-dots';
    /** @type {HTMLButtonElement[]} */
    const dotEls = [];
    for (let i = 0; i < slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'qa-dot';
      dot.setAttribute('aria-label', `${i + 1}`);
      dot.addEventListener(
        'click',
        () => scroller.scrollTo({ left: i * scroller.clientWidth, behavior: 'smooth' }),
        { signal }
      );
      dots.appendChild(dot);
      dotEls.push(dot);
    }
    media.appendChild(dots);

    let raf = 0;
    const setActive = () => {
      const width = scroller.clientWidth || 1;
      const index = Math.round(scroller.scrollLeft / width);
      dotEls.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
    scroller.addEventListener(
      'scroll',
      () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          setActive();
        });
      },
      { signal }
    );
    setActive();
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
